import { useSelector } from 'react-redux';
import { withTheme } from 'styled-components';
import { FovTypography, HorizontalSpacer, LampOutlined, FovCheckbox } from 'components';
import { CreditsOutlined, UserOutlined } from 'components/icons';
import { allRoles, superUsers } from 'components/generic/ProtectedContent';
import { getUsersAdmin, clearAdminModels } from 'store/actions/admin';
import {
    NameContainer, IconButton, RoleHeaderContainer, RoleHeaderLabel,
    RoleFilterWrapper, RoleFilterSelect, ColumnSortSelect, CountContainer, ConnectionBadge, LoginAsButton,
} from './styles';

const RoleColumnHeader = withTheme(({ theme, searchParams, setSearchParams, dispatch, token }) => {
    const selectedTheme = useSelector(state => state.userData.userPreferences.theme);

    return (
    <RoleHeaderContainer>
        <RoleHeaderLabel theme={theme} $selectedTheme={selectedTheme}>Role</RoleHeaderLabel>
        <RoleFilterWrapper theme={theme} $selectedTheme={selectedTheme}>
            <RoleFilterSelect
                theme={theme}
                $selectedTheme={selectedTheme}
                value={searchParams?.get('role') || ''}
                onChange={e => {
                    const newVal = e.target.value;
                    if (newVal) {
                        searchParams.set('role', newVal);
                    } else {
                        searchParams.delete('role');
                    }
                    searchParams.set('page', 1);
                    setSearchParams(searchParams);
                    dispatch(getUsersAdmin(token));
                }}
            >
                <option value=''>All</option>
                {allRoles.map(role => (
                    <option key={role} value={role}>{role}</option>
                ))}
            </RoleFilterSelect>
        </RoleFilterWrapper>
    </RoleHeaderContainer>
    );
});

export const COLUMN_SORT_FIELDS = [ 'model_count', 'credits' ];
export const LEGACY_SORT_PARAMS = [ 'orderby', 'order', 'meta_key', 'meta_type' ];
const sortEntryField = entry => entry.split(':')[0];

const SortColumnHeader = withTheme(({ theme, label, field, searchParams, setSearchParams, dispatch, token }) => {
    const selectedTheme = useSelector(state => state.userData.userPreferences.theme);
    const entries = (searchParams?.get('sort') || '').split(',').filter(Boolean);
    const currentDir = entries.find(entry => sortEntryField(entry) === field)?.split(':')[1] || '';

    return (
    <RoleHeaderContainer>
        <RoleHeaderLabel theme={theme} $selectedTheme={selectedTheme}>{label}</RoleHeaderLabel>
        <RoleFilterWrapper theme={theme} $selectedTheme={selectedTheme}>
            <ColumnSortSelect
                theme={theme}
                $selectedTheme={selectedTheme}
                value={currentDir}
                onChange={e => {
                    const newDir = e.target.value;
                    const others = entries.filter(entry => sortEntryField(entry) !== field);
                    const columnSorts = others.filter(entry => COLUMN_SORT_FIELDS.includes(sortEntryField(entry)));
                    const mainSorts = others.filter(entry => !COLUMN_SORT_FIELDS.includes(sortEntryField(entry)));
                    let next = others;
                    if (newDir && currentDir) {
                        next = entries.map(entry => sortEntryField(entry) === field ? `${field}:${newDir}` : entry);
                    } else if (newDir) {
                        next = [ ...columnSorts, `${field}:${newDir}`, ...mainSorts ];
                    }
                    LEGACY_SORT_PARAMS.forEach(key => searchParams.delete(key));
                    next.length ? searchParams.set('sort', next.join(',')) : searchParams.delete('sort');
                    searchParams.set('page', 1);
                    setSearchParams(searchParams);
                    dispatch(getUsersAdmin(token));
                }}
            >
                <option value=''>—</option>
                <option value='desc'>↓</option>
                <option value='asc'>↑</option>
            </ColumnSortSelect>
        </RoleFilterWrapper>
    </RoleHeaderContainer>
    );
});

export const listColumns = (
    theme,
    selectedTheme,
    setCreditsUser,
    navigate,
    searchParams,
    setSearchParams,
    dispatch,
    token,
    showSelection,
    selectedUserIds,
    onToggleUser,
    onToggleAll,
    allSelected,
    setImpersonateUser) => ([
    ...(showSelection ? [ {
        title: (
            <FovCheckbox
                checked={allSelected}
                onChange={onToggleAll}
            />
        ),
        key: 'select',
        width: 50,
        alignContent: 'center',
        render: (text, user) => (
            <FovCheckbox
                checked={selectedUserIds.includes(user.id)}
                onChange={() => onToggleUser(user.id)}
            />
        ),
    } ] : []),
    {
        title: 'Name, Email, ID',
        key: 'email',
        width: 300,
        alignContent: 'left',
        render: (text, user) => {
            const isAdmin = superUsers.includes(user.role);
            return (
                <NameContainer>
                    <div>{user.last_name}, {user.first_name}</div>
                    <div>{user.email}</div>
                    <div>ID: {user.id}</div>
                    <LoginAsButton
                        theme={theme}
                        $selectedTheme={selectedTheme}
                        disabled={isAdmin}
                        title={isAdmin
                            ? 'Cannot log in as an administrator.'
                            : `Log in as ${user.first_name || user.email}`}
                        onClick={() => setImpersonateUser?.(user)}
                    >
                        <UserOutlined width={13} height={13} color='currentColor' />
                        Log in as user
                    </LoginAsButton>
                </NameContainer>
            );
        },
    },
    {
        title: <RoleColumnHeader
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        dispatch={dispatch} token={token} />,
        key: 'role',
        width: 120,
        render: (text, user) => (
            <FovTypography>{user.role}</FovTypography>
        ),
    },
    {
        title: <SortColumnHeader
        label='Credits' field='credits'
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        dispatch={dispatch} token={token} />,
        key: 'credits',
        width: 120,
        render: (text, user) => (
            <IconButton
                theme={theme}
                $selectedTheme={selectedTheme}
                onClick={() => setCreditsUser(user)}
                icon={(
                    <CountContainer>
                        <CreditsOutlined />
                        <HorizontalSpacer size={5} />
                        {user.meta_data.find(data => data.key === 'av_points')?.value || 0}
                    </CountContainer>
                )}
            />
        ),
    },
    {
        title: <SortColumnHeader
        label='Models' field='model_count'
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        dispatch={dispatch} token={token} />,
        key: 'models',
        width: 120,
        render: (text, user) => (
            <IconButton
                theme={theme}
                $selectedTheme={selectedTheme}
                disabled={!user.modelCount}
                onClick={() => {
                    dispatch(clearAdminModels());
                    navigate(`/archvision-admin/models?search_userId=${user.id}`); }}
                icon={(
                    <CountContainer>
                        <LampOutlined />
                        <HorizontalSpacer size={5} />
                        {user.modelCount}
                    </CountContainer>
                )}
            />
        ),
    },
    {
        title: 'AVS-AVAIL Connection',
        key: 'AVS_AVAIL',
        width: 50,
        alignContent: 'center',
        render: (text, user) => {
            const connected = !!user.meta_data.find(data => data.key === 'avail_refresh_token');
            return (
                <ConnectionBadge $connected={connected} $selectedTheme={selectedTheme}>
                    {connected ? 'Connected' : 'Not Connected'}
                </ConnectionBadge>
            );
        },
    },
    {
        title: 'Registered',
        key: 'date_created',
        width: 100,
        render: (text, user) => (
            <FovTypography>
                {new Date(user.date_created).toLocaleDateString(
                    'en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
            </FovTypography>
        ),
    },
]);
